// ============================================================================
// FNE-BRIDGE Frontend - Hook thème (light/dark)
// Uses a module-level shared state so that toggling in any component
// instantly re-renders ALL components using this hook (e.g. App.tsx + MainLayout).
// ============================================================================

import { useMemo, useSyncExternalStore, useCallback } from 'react';
import { STORAGE_KEYS } from '../config/constants';
import { lightTheme, darkTheme } from '../assets/theme';

export type ThemeMode = 'light' | 'dark';

// --- Module-level shared state ---
const readInitialMode = (): ThemeMode => {
  try {
    const stored = localStorage.getItem(STORAGE_KEYS.THEME_MODE);
    if (stored) {
      const parsed = JSON.parse(stored);
      if (parsed === 'light' || parsed === 'dark') return parsed;
    }
  } catch {
    // ignore
  }
  return 'light';
};

let currentMode: ThemeMode = readInitialMode();
const listeners = new Set<() => void>();

const subscribe = (listener: () => void) => {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
};

const getSnapshot = () => currentMode;

const setMode = (value: ThemeMode | ((prev: ThemeMode) => ThemeMode)) => {
  const newValue = typeof value === 'function' ? (value as (prev: ThemeMode) => ThemeMode)(currentMode) : value;
  if (newValue === currentMode) return;
  currentMode = newValue;
  try {
    localStorage.setItem(STORAGE_KEYS.THEME_MODE, JSON.stringify(newValue));
  } catch {
    // ignore
  }
  listeners.forEach((l) => l());
};

// Sync across browser tabs
if (typeof window !== 'undefined') {
  window.addEventListener('storage', (e) => {
    if (e.key !== STORAGE_KEYS.THEME_MODE || !e.newValue) return;
    try {
      const parsed = JSON.parse(e.newValue);
      if ((parsed === 'light' || parsed === 'dark') && parsed !== currentMode) {
        currentMode = parsed;
        listeners.forEach((l) => l());
      }
    } catch {
      // ignore
    }
  });
}

export function useThemeMode() {
  const mode = useSyncExternalStore(subscribe, getSnapshot, getSnapshot);
  const theme = useMemo(() => (mode === 'dark' ? darkTheme : lightTheme), [mode]);
  const toggleTheme = useCallback(() => setMode((prev) => (prev === 'light' ? 'dark' : 'light')), []);
  return { mode, theme, toggleTheme, setMode };
}
